import type { AgeData, DateRange, ShowFilter } from '@/types';
import { uppercaseFirstLetter } from '@/utils';
import { Button, Loader } from '@acast-tech/decibel';
import { ArrowDownToLineLightGlyph, ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';
import Modal from '@/components/modals/Modal/Modal';
import { useAgePlaysQuery } from '@/services/revenueApi';
import { useDerivedNetworkContext } from '@/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NoDataMessage from '@/components/states/NoDataMessage';
import { twMerge } from 'tailwind-merge';
import useAgeCsvExport from './useAgeCsvExport';
import {
  trackAudienceAgeModalDownloadClicked,
  trackAudienceAgeModalOpened,
} from './audienceModalMixpanel';

// TODO: replace with Echo tokens
const COLOR_CLASSES = [
  'bg-white',
  'bg-backgroundLightPurple',
  'bg-backgroundPurple',
  'bg-chartColors-color6',
  'bg-chartColors-color5',
  'bg-chartColors-color4',
] as const;

/**
 * Returns background + text color classes for a percentage value.
 * Buckets are 10% wide, anchored from the top of the data range. The top
 * bucket gets the darkest color; rows more than 6 buckets below the max
 * receive no color.
 */
const getPercentageColorClasses = (percentage: number, globalMax: number): string => {
  const totalBuckets = Math.ceil(globalMax / 10);
  const topBucketIndex = totalBuckets - 1;
  const bucketIndex = Math.min(Math.floor(percentage / 10), topBucketIndex);
  const bucketsFromTop = topBucketIndex - bucketIndex;
  if (bucketsFromTop >= 6) return '';
  return COLOR_CLASSES[5 - bucketsFromTop];
};

/**
 * Renders a horizontal legend showing the up-to-6 colored percentage bands
 * derived from the global max across all platform cards.
 */
const ColorBandLegend = ({ globalMax }: { globalMax: number }) => {
  const totalBuckets = Math.ceil(globalMax / 10);
  const topBucketIndex = totalBuckets - 1;
  const coloredBucketCount = Math.min(6, totalBuckets);

  const buckets = Array.from({ length: coloredBucketCount }, (_, colorRank) => {
    const bucketIndex = topBucketIndex - colorRank;
    const lower = bucketIndex * 10;
    const upper = colorRank === 0 ? globalMax : (bucketIndex + 1) * 10;
    const bgClass = COLOR_CLASSES[5 - colorRank].split(' ')[0];
    return { bgClass, lower, upper };
  }).reverse();

  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
      {buckets.map(({ bgClass, lower, upper }) => (
        <div key={lower} className="flex items-center gap-1.5">
          <div className={twMerge('w-3 h-3 rounded-sm flex-shrink-0', bgClass)} />
          <span className="text-xs text-textMedGrey">
            {lower}–{upper}%
          </span>
        </div>
      ))}
    </div>
  );
};

const AgePlatformCard = ({
  platform,
  ageDistribution,
  medianAge,
  globalMax,
}: {
  platform: string;
  ageDistribution: AgeData[];
  medianAge: number;
  globalMax: number;
}) => {
  return (
    <div className="border rounded-lg overflow-hidden flex flex-col">
      <div className="p-4">
        <h3 className="font-medium">{uppercaseFirstLetter(platform.replace(/_/g, ' '))}</h3>
        <p className="text-sm text-textMedGrey">Median age: {medianAge}</p>
      </div>
      <ul className="flex flex-col list-none border-t">
        {ageDistribution.map(({ label, percentage }) => (
          <li
            key={label}
            className={twMerge(
              'border-b',
              'flex flex-row justify-between gap-2 px-4 py-2',
              getPercentageColorClasses(percentage, globalMax)
            )}
          >
            <span className="text-sm text-right">{label}</span>
            <span className="text-sm text-right">{percentage}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AgeModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useAgePlaysQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources }
  );

  if (isPending) {
    return (
      <section className="px-6 py-10 flex flex-col justify-center items-center h-[60vh] gap-10">
        <Loader data-testid="loader" />
      </section>
    );
  }

  if (!data || data.plays.length < 1) {
    return <NoDataMessage dataType="age data" />;
  }

  const globalMax = Math.max(
    ...data.plays.flatMap(({ age_distribution }) =>
      age_distribution.map(({ percentage }) => percentage)
    ),
    1
  );

  return (
    <section className="px-6 py-10 flex flex-col gap-6">
      <ColorBandLegend globalMax={globalMax} />
      <div className="flex flex-wrap justify-center gap-6 [&>*]:flex-1 [&>*]:min-w-[220px] [&>*]:max-w-[25%]">
        {data.plays.map(({ platform, age_distribution, median_age }) => (
          <AgePlatformCard
            key={platform}
            platform={platform}
            ageDistribution={age_distribution}
            medianAge={median_age}
            globalMax={globalMax}
          />
        ))}
      </div>
    </section>
  );
};

const AgeModal = () => {
  const { handleCSVExport } = useAgeCsvExport();

  return (
    <Modal
      hashProp="age-details"
      title="Age details"
      showDateRangeFilter
      onOpen={trackAudienceAgeModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceAgeModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export data',
        downloadButtonIcon: ArrowDownToLineLightGlyph,
      }}
      renderTrigger={(openModal) => (
        <div className="flex flex-row justify-end self-end">
          <Button
            className="self-right"
            variant="tertiary"
            glyphEnd={ChevronRightRegularGlyph}
            onClick={openModal}
          >
            More details
          </Button>
        </div>
      )}
    >
      {(dateRange, selectedShow) => (
        <AgeModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </Modal>
  );
};

export default AgeModal;
